<script setup lang="ts">
import { LucideInfo } from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import type { ICreditCalculatorProps } from './types'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'
import { useCurrencyProp } from '~/composables/useCurrencyProp'
import { ANCHOR_IDS } from '~/lib/constants'
import { ContentBlock } from '~/components/ui/content-block'

const props = defineProps<ICreditCalculatorProps>()

// Simple form schema without amount validation messages (handled by custom validator)
const { t } = useI18n()

const formSchema = toTypedSchema(
  z.object({
    amount: z.number().optional(), // Remove default validation messages
    currency: z.number({ message: t('feature.calculator.credit.select_currency') }),
    period: z.array(z.number().min(1).max(120))
  })
)

const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    amount: props.credit?.rates?.[0]?.amount_from || 100000,
    currency: props.credit?.rates?.[0]?.currency_id || 1,
    period: [3] // Start with 3 months as default
  }
})

// Helper function to get current currency rates
const getCurrentCurrencyRates = () => {
  return props.credit?.rates?.filter((rate) => rate.currency_id === form.values.currency) || []
}

// Custom validation messages that update with currency
const amountValidationMessage = computed(() => {
  const currentCurrencyRates = getCurrentCurrencyRates()
  const amount = form.values.amount
  const currencyCode = useCurrencyProp(form.values.currency, 'code')

  // Check if amount is empty or invalid
  if (!amount || amount <= 0) {
    return t('feature.calculator.credit.enter_loan_amount')
  }

  // Check currency limits
  if (currentCurrencyRates.length > 0) {
    const validMinAmounts = currentCurrencyRates
      .map((rate) => rate.amount_from)
      .filter((amount) => amount && amount > 0)
    const validMaxAmounts = currentCurrencyRates
      .map((rate) => rate.amount_to)
      .filter((amount) => amount && amount > 0)

    if (validMinAmounts.length > 0) {
      const minAmount = Math.min(...validMinAmounts)
      if (amount < minAmount) {
        return `${t('feature.calculator.credit.min_amount')}: ${minAmount.toLocaleString('ru-RU')} ${currencyCode}`
      }
    }

    if (validMaxAmounts.length > 0) {
      const maxAmount = Math.max(...validMaxAmounts)
      if (amount > maxAmount) {
        return `${t('feature.calculator.credit.max_amount')}: ${maxAmount.toLocaleString('ru-RU')} ${currencyCode}`
      }
    }
  }

  return ''
})

// Update initial period to use appropriate starting period after form is created
watchEffect(() => {
  const currentCurrencyRates =
    props.credit?.rates?.filter((rate) => rate.currency_id === form.values.currency) || []

  if (currentCurrencyRates.length > 0) {
    const periods = currentCurrencyRates.map((rate) => rate.period)
    const currentPeriod = form.values.period?.[0] || 0

    let targetPeriod
    if (periods.length === 1) {
      // For single period currencies, ensure we start at minimum 3 months
      targetPeriod = Math.max(3, currentPeriod)
    } else {
      // For multiple periods, use actual minimum available period
      const minAvailablePeriod = Math.min(...periods)
      targetPeriod = Math.max(minAvailablePeriod, currentPeriod)
    }

    // Update period if needed
    if (currentPeriod < targetPeriod) {
      form.setFieldValue('period', [targetPeriod])
    }
  }
})

const availableRates = computed(() => {
  if (!props.credit?.rates || !Array.isArray(props.credit.rates)) {
    return []
  }
  return props.credit.rates.filter((rate) => rate.currency_id === form.values.currency)
})

const availableCurrencyIds = computed(() => {
  if (!props.credit?.rates || !Array.isArray(props.credit.rates)) {
    return []
  }
  const uniqueIds = [...new Set(props.credit.rates.map((rate) => rate.currency_id))]
  return uniqueIds
})

const currentRate = computed(() => {
  if (!availableRates.value.length) {
    return 0
  }

  const selectedPeriod = form.values.period?.[0]
  if (!selectedPeriod) {
    return 0
  }

  // Always find the closest matching period from available rates
  const closestRate = availableRates.value.reduce((closest, rate) => {
    const currentDiff = Math.abs(rate.period - selectedPeriod)
    const closestDiff = Math.abs(closest.period - selectedPeriod)
    return currentDiff < closestDiff ? rate : closest
  })

  if (closestRate) {
    const rate = parseFloat(closestRate.rate)
    return !isNaN(rate) ? rate : 0
  }

  return 0
})

// Calculate monthly payment using standard loan formula
const monthlyPayment = computed(() => {
  const principal = form.values.amount || 0
  const annualRate = currentRate.value || 0
  const months = form.values.period?.[0] || 1

  if (isNaN(principal) || isNaN(annualRate) || isNaN(months) || annualRate === 0) {
    return principal / months // Simple division if no interest rate
  }

  // Convert annual rate to monthly decimal rate
  const monthlyRate = annualRate / 100 / 12

  // Standard loan payment formula: P * (r * (1+r)^n) / ((1+r)^n - 1)
  const factor = Math.pow(1 + monthlyRate, months)
  const payment = (principal * monthlyRate * factor) / (factor - 1)

  return !isNaN(payment) ? payment : 0
})

const totalPayment = computed(() => {
  const monthly = monthlyPayment.value || 0
  const months = form.values.period?.[0] || 1

  if (isNaN(monthly) || isNaN(months)) {
    return 0
  }

  return monthly * months
})

const totalInterest = computed(() => {
  const total = totalPayment.value || 0
  const principal = form.values.amount || 0

  if (isNaN(total) || isNaN(principal)) {
    return 0
  }

  return total - principal
})

const maxPeriod = computed(() => {
  const periods = availableRates.value.map((rate) => rate.period)
  return periods.length > 0 ? Math.max(...periods) : 60
})

const minPeriod = computed(() => {
  const periods = availableRates.value.map((rate) => rate.period)
  if (periods.length === 0) return 3
  if (periods.length === 1) return 3 // Use 3 months minimum when only one period available
  return Math.min(...periods)
})

const stepPeriod = computed(() => {
  const periods = availableRates.value.map((rate) => rate.period)
  if (periods.length <= 1) return 3 // Use 3-month steps when only one or no periods available
  return 1
})

const minAmount = computed(() => {
  const currentCurrencyRates = availableRates.value
  if (currentCurrencyRates.length === 0) return null
  const validMinAmounts = currentCurrencyRates
    .map((rate) => rate.amount_from)
    .filter((amount) => amount && amount > 0)
  return validMinAmounts.length > 0 ? Math.min(...validMinAmounts) : null
})

const maxAmount = computed(() => {
  const currentCurrencyRates = availableRates.value
  if (currentCurrencyRates.length === 0) return null
  const validMaxAmounts = currentCurrencyRates
    .map((rate) => rate.amount_to)
    .filter((amount) => amount && amount > 0)
  return validMaxAmounts.length > 0 ? Math.max(...validMaxAmounts) : null
})

// Show only the limits the bank has set instead of placeholder values
const amountLimitsText = computed(() => {
  const currency = form.values.currency || 1
  const from = minAmount.value ? useCurrencyFormat(minAmount.value, currency) : null
  const to = maxAmount.value ? useCurrencyFormat(maxAmount.value, currency) : null

  if (from && to) return t('feature.calculator.credit.amount_range', { from, to })
  if (from) return t('feature.calculator.credit.amount_range_from', { from })
  if (to) return t('feature.calculator.credit.amount_range_to', { to })
  return t('feature.calculator.credit.no_limit')
})

const onInputChange = () => {
  nextTick(() => {
    form.validateField('amount')
  })
}

const onAmountInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = Number(target.value)
  if (value < 0) {
    target.value = '0'
    form.setFieldValue('amount', 0)
  } else if (maxAmount.value && value > maxAmount.value) {
    target.value = String(maxAmount.value)
    form.setFieldValue('amount', maxAmount.value)
  }
  onInputChange()
}

watch(
  () => form.values.currency,
  () => {
    if (!props.credit?.rates || !Array.isArray(props.credit.rates)) {
      return
    }

    const newCurrencyRates = props.credit.rates.filter(
      (rate) => rate.currency_id === form.values.currency
    )
    if (newCurrencyRates.length > 0) {
      // Set amount to minimum for this currency
      const minAmount = Math.min(...newCurrencyRates.map((rate) => rate.amount_from))
      form.setFieldValue('amount', minAmount)

      // Set period based on available periods for this currency
      const periods = newCurrencyRates.map((rate) => rate.period)
      let initialPeriod
      if (periods.length === 1) {
        initialPeriod = 3 // Start with 3 months when only one period available
      } else {
        initialPeriod = Math.min(...periods)
      }
      form.setFieldValue('period', [initialPeriod])

      // Trigger validation for amount field when currency changes
      form.validateField('amount')
    }
  }
)
</script>

<template>
  <ContentBlock
    :id="ANCHOR_IDS.FEATURE_CREDIT_CALCULATOR"
    :heading-text="props.headingText"
    :heading-tag="props.headingTag"
    :heading-class="props.headingClass"
    :show-link-icon="true"
    :variant="props.variant"
    class="FormScrollOffset"
  >
    <Content>
      <p class="mb-6 text-gray-600">
        {{ $t('page.credits.detail.calculator_description', { title: props.credit?.title }) }}
      </p>
    </Content>
    <div class="flex flex-col items-center gap-6 md:flex-row">
      <div class="flex w-full flex-col gap-5 md:min-w-[400px]">
        <FormField v-slot="{ componentField }" name="amount">
          <FormItem>
            <FormLabel>{{ $t('common.credit_amount') }}</FormLabel>
            <FormControl>
              <Input
                type="number"
                :min="minAmount || 0"
                :max="maxAmount || undefined"
                v-bind="componentField"
                @input="onAmountInput"
              />
            </FormControl>
            <FormDescription class="text-sm text-gray-500">
              {{ amountLimitsText }}
            </FormDescription>
            <div v-if="amountValidationMessage" class="mt-1 text-sm text-red-500">
              {{ amountValidationMessage }}
            </div>
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="currency">
          <FormItem>
            <FormLabel>{{ $t('feature.calculator.credit.currency') }}</FormLabel>
            <FormControl>
              <Select v-bind="componentField" @update:model-value="onInputChange">
                <SelectTrigger :aria-label="$t('feature.calculator.credit.select_currency')">
                  <SelectValue :placeholder="$t('feature.calculator.credit.select_currency')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="currencyId in availableCurrencyIds"
                    :key="currencyId"
                    :value="currencyId"
                  >
                    {{ useCurrencyProp(currencyId, 'code') }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="period">
          <FormItem class="space-y-3">
            <FormLabel>{{ $t('feature.calculator.credit.loan_term') }}</FormLabel>
            <FormControl>
              <Slider
                :model-value="componentField.modelValue"
                :max="maxPeriod"
                :min="minPeriod"
                :step="stepPeriod"
                :name="componentField.name"
                :aria-label-name="$t('feature.calculator.credit.loan_term')"
                @value-commit="onInputChange"
                @update:model-value="
                  (value) => {
                    if (componentField['onUpdate:modelValue']) {
                      componentField['onUpdate:modelValue'](value)
                    }
                    onInputChange()
                  }
                "
              />
              <FormDescription class="flex justify-between text-gray-500">
                <span>{{ minPeriod }} {{ $t('feature.calculator.credit.month_short') }}.</span>
                <span
                  >{{ form.values.period?.[0] || minPeriod }}
                  {{ $t('feature.calculator.credit.month_short') }}.</span
                >
                <span>{{ maxPeriod }} {{ $t('feature.calculator.credit.month_short') }}.</span>
              </FormDescription>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <div class="hidden items-center gap-2.5 text-sm md:flex">
          <LucideInfo class="min-w-5 text-yellow-400" />
          {{ $t('feature.calculator.disclaimer') }}
        </div>
      </div>

      <div class="flex w-full flex-col items-center gap-4 md:text-center">
        <div class="text-center">
          <div class="mb-2 text-gray-600">{{ $t('feature.calculator.credit.interest_rate') }}</div>
          <div class="mb-4 text-2xl font-bold text-blue-600">
            {{ (currentRate || 0).toFixed(2) }}%
          </div>
        </div>

        <div class="text-center">
          <div class="mb-2 text-gray-600">
            {{ $t('feature.calculator.credit.monthly_payment') }}
          </div>
          <div class="mb-4 text-2xl font-bold text-orange-600">
            {{ useCurrencyFormat(monthlyPayment || 0, form.values.currency || 1) }}
          </div>
        </div>

        <div class="text-center">
          <div class="mb-2 text-gray-600">{{ $t('feature.calculator.credit.overpayment') }}</div>
          <div class="mb-4 text-xl font-bold text-red-600">
            {{ useCurrencyFormat(totalInterest || 0, form.values.currency || 1) }}
          </div>
        </div>

        <div class="text-center">
          <div class="mb-2 text-gray-600">{{ $t('feature.calculator.credit.total_to_pay') }}</div>
          <div class="text-3xl font-bold text-blue-900">
            {{ useCurrencyFormat(totalPayment || 0, form.values.currency || 1) }}
          </div>
        </div>

        <div class="mt-4 flex items-start gap-2.5 text-xs md:hidden">
          <LucideInfo class="min-w-5 text-yellow-400" />
          {{ $t('feature.calculator.disclaimer') }}
        </div>
      </div>
    </div>
  </ContentBlock>
</template>
